import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState } from '@/store/store'
import { Application } from '@/types/application'
import { Gig } from '@/types/gig'
import {
    FiBriefcase,
    FiUser,
    FiMapPin,
    FiClock,
    FiMessageSquare,
    FiEye,
    FiCheck,
    FiX,
    FiSearch,
    FiStar,
    FiCheckCircle,
    FiAlertCircle,
    FiXCircle,
    FiMoreVertical,
    FiCalendar,
    FiTrendingUp,
    FiRefreshCw
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Avatar from '@/components/ui/Avatar'
import SelectDropdown from '@/components/ui/SelectDropdown'
import { formatDate } from '@/utils/formatters'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { applicationApi } from '@/services/api/applications'
import { messageApi } from '@/services/api/messages'
import MessageModal from '@/components/ui/MessageModal'
import WorkSubmissionModal from '@/components/ui/WorkSubmissionModal'
import ReviewModal from '@/components/ui/ReviewModal'
import CountdownTimer from '@/components/ui/CountdownTimer'
import { CurrencyDisplay } from '@/components/currency'
import { socketService } from '@/services/socketService'
import EscrowStatus from '@/components/ui/EscrowStatus'

// Type guard to check if gig is populated
const isGigPopulated = (gig: Application['gig']): gig is Gig => {
    return gig !== null && typeof gig === 'object' && '_id' in gig
}

// Helper to get category name whether it's string or object
const getCategoryName = (category: any) => {
    if (!category) return 'Unknown'
    if (typeof category === 'string') {
        return category
    }
    return category.name || category.slug || 'Unknown'
}

const ApplicationsPage: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth)
    const navigate = useNavigate()

    const [applications, setApplications] = useState<Application[]>([])
    const [loading, setLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'in_progress' | 'submitted' | 'under_review' | 'approved' | 'rejected' | 'completed'>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'amount'>('newest')
    const [expandedCard, setExpandedCard] = useState<string | null>(null)

    // Message modal state
    const [messageModal, setMessageModal] = useState<{
        isOpen: boolean
        recipientId: string
        recipientName: string
        context: string
        applicationId: string
    }>({
        isOpen: false,
        recipientId: '',
        recipientName: '',
        context: '',
        applicationId: ''
    })

    // Work submission modal state
    const [workSubmissionModal, setWorkSubmissionModal] = useState<{
        isOpen: boolean
        application: Application | null
        gigTitle: string
    }>({
        isOpen: false,
        application: null,
        gigTitle: ''
    })

    // Review modal state
    const [reviewModal, setReviewModal] = useState<{
        isOpen: boolean
        application: Application | null
        workerName: string
        gigTitle: string
    }>({
        isOpen: false,
        application: null,
        workerName: '',
        gigTitle: ''
    })

    const { showSuccess, showError } = useSnackbarContext()

    // Fetch applications from API
    useEffect(() => {
        const fetchApplications = async () => {
            if (!user) return

            try {
                setLoading(true)
                // Call different endpoints based on user role
                const response = user.role === 'worker'
                    ? await applicationApi.getSentApplications()
                    : await applicationApi.getReceivedApplications()

                // Handle both possible response formats - backend returns {success, data: {applications: []}}
                const applications = Array.isArray(response.data)
                    ? response.data
                    // @ts-expect-error - Intentional nested data structure for API response
                    : (response.data.data?.applications || response.data.applications || [])

                setApplications(applications)
            } catch (error) {
                console.error('Failed to fetch applications:', error)
                showError('Failed to load applications')
                setApplications([]) // Ensure applications is always an array
            } finally {
                setLoading(false)
            }
        }

        fetchApplications()
    }, [user])

    const handleRefreshApplications = async () => {
        if (!user) return

        try {
            setIsRefreshing(true)
            // Call different endpoints based on user role
            const response = user.role === 'worker'
                ? await applicationApi.getSentApplications()
                : await applicationApi.getReceivedApplications()

            // Handle both possible response formats - backend returns {success, data: {applications: []}}
            const applications = Array.isArray(response.data)
                ? response.data
                // @ts-expect-error - Intentional nested data structure for API response
                : (response.data.data?.applications || response.data.applications || [])

            setApplications(applications)
            showSuccess('Applications refreshed successfully')
        } catch (error) {
            console.error('Failed to refresh applications:', error)
            showError('Failed to refresh applications')
        } finally {
            setIsRefreshing(false)
        }
    }

    const handleStatusUpdate = async (applicationId: string, newStatus: 'accepted' | 'rejected') => {
        // Store original state for potential revert
        const originalApplications = applications

        try {
            // Optimistically update UI
            setApplications(prev => (prev || []).map(app =>
                app._id === applicationId
                    ? { ...app, status: newStatus }
                    : app
            ))

            // Make actual API call
            await applicationApi.updateApplicationStatus(applicationId, newStatus)

            // Show different messages based on status and notify about work starting
            if (newStatus === 'accepted') {
                showSuccess(`Application accepted successfully! The worker has been notified that work can begin.`)
            } else {
                showSuccess(`Application ${newStatus} successfully`)
            }

        } catch (error: any) {
            console.error('Failed to update application status:', error)
            const errorDetails = error.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : error.response?.data?.error?.message || `Failed to ${newStatus} application`
            showError(errorMessage)

            // Revert on error
            setApplications(originalApplications)
        }
    }

    // Function to open message modal for employer messaging worker
    const handleStartConversation = (workerId: string, workerName: string, gigTitle: string, applicationId: string) => {
        setMessageModal({
            isOpen: true,
            recipientId: workerId,
            recipientName: workerName,
            context: `Regarding their application for "${gigTitle}"`,
            applicationId
        })
    }

    // Function to open message modal for worker messaging employer
    const handleMessageEmployer = (employerId: string, employerName: string, gigTitle: string, applicationId: string) => {
        setMessageModal({
            isOpen: true,
            recipientId: employerId,
            recipientName: employerName,
            context: `Regarding your application for "${gigTitle}"`,
            applicationId
        })
    }

    // Function to handle sending the actual message
    const handleSendMessage = async (message: string) => {
        try {
            // Create or get existing conversation
            const response = await messageApi.createConversation({
                receiverId: messageModal.recipientId,
                type: 'application',
                initialMessage: message,
                metadata: { applicationId: messageModal.applicationId }
            })

            if (response.data?.conversation?._id) {
                // Navigate to messages with the conversation ID
                navigate(`/messages?conversationId=${response.data.conversation._id}`)
                showSuccess('Message sent successfully!')
            }
        } catch (error: any) {
            console.error('Failed to send message:', error)
            const errorDetails = error.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : error.response?.data?.error?.message || 'Failed to send message'

            // If conversation already exists, try to find it and navigate
            if (error.response?.status === 409 || errorMessage.includes('already exists')) {
                try {
                    const conversationsResponse = await messageApi.getConversations()
                    const existingConversation = conversationsResponse.data.conversations.find(
                        conv => conv.participants.some((p: any) => p._id === messageModal.recipientId || p === messageModal.recipientId) &&
                            conv.metadata?.applicationId === messageModal.applicationId
                    )

                    if (existingConversation) {
                        navigate(`/messages?conversationId=${existingConversation._id}`)
                        showSuccess('Opening existing conversation')
                        return
                    }
                } catch (findError) {
                    console.error('Failed to find existing conversation:', findError)
                }

                // Fallback: just navigate to messages page
                navigate('/messages')
                showError('Conversation may already exist. Please check your messages.')
            } else {
                showError(errorMessage)
            }
        }
    }

    // Function to close the message modal
    const handleCloseMessageModal = () => {
        setMessageModal({
            isOpen: false,
            recipientId: '',
            recipientName: '',
            context: '',
            applicationId: ''
        })
    }

    // Function to open work submission modal
    const handleOpenWorkSubmission = (application: Application, gigTitle: string) => {
        setWorkSubmissionModal({
            isOpen: true,
            application,
            gigTitle
        })
    }

    // Function to close work submission modal
    const handleCloseWorkSubmissionModal = () => {
        setWorkSubmissionModal({
            isOpen: false,
            application: null,
            gigTitle: ''
        })
    }

    // Function to submit work
    const handleSubmitWork = async (data: { deliveryUrls: string[], description: string }) => {
        if (!workSubmissionModal.application) return

        try {
            await applicationApi.submitWork(workSubmissionModal.application._id, data)

            // Update local state
            setApplications(prev => prev.map(app =>
                app._id === workSubmissionModal.application!._id
                    ? { ...app, status: 'submitted' as const, workSubmission: { ...data, submittedAt: new Date() } }
                    : app
            ))

            // Emit socket event for work submission
            const workerName = typeof workSubmissionModal.application.worker === 'object'
                ? `${workSubmissionModal.application.worker.profile?.firstName} ${workSubmissionModal.application.worker.profile?.lastName}`.trim() || 'Worker'
                : 'Worker'
            const gigTitle = workSubmissionModal.gigTitle

            socketService.emitWorkSubmitted?.({
                applicationId: workSubmissionModal.application._id,
                workerName,
                gigTitle
            })

            showSuccess('Work submitted successfully! The employer will review it within 14 days.')
        } catch (error: any) {
            console.error('Failed to submit work:', error)
            const errorDetails = error.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : error.response?.data?.error?.message || 'Failed to submit work'
            showError(errorMessage)
            throw error // Re-throw to let modal handle it
        }
    }

    // Function to open review modal
    const handleOpenReview = (application: Application, workerName: string, gigTitle: string) => {
        setReviewModal({
            isOpen: true,
            application,
            workerName,
            gigTitle
        })
    }

    // Function to close review modal
    const handleCloseReviewModal = () => {
        setReviewModal({
            isOpen: false,
            application: null,
            workerName: '',
            gigTitle: ''
        })
    }

    // Function to submit review
    const handleSubmitReview = async (decision: 'approved' | 'rejected', feedback?: string) => {
        if (!reviewModal.application) return

        try {
            await applicationApi.reviewWork(reviewModal.application._id, { decision, feedback })

            // Update local state
            setApplications(prev => prev.map(app =>
                app._id === reviewModal.application!._id
                    ? {
                        ...app,
                        status: decision === 'approved' ? 'approved' as const : 'rejected' as const,
                        review: {
                            reviewedAt: new Date(),
                            reviewedBy: 'employer' as const,
                            decision: decision === 'approved' ? 'approved' as const : 'rejected' as const,
                            feedback,
                            autoApprovalDeadline: app.review?.autoApprovalDeadline || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
                        }
                    }
                    : app
            ))

            // Emit socket event for work review
            const employerName = user?.profile?.firstName && user?.profile?.lastName
                ? `${user.profile.firstName} ${user.profile.lastName}`.trim()
                : 'Employer'

            socketService.emitWorkReviewed?.({
                applicationId: reviewModal.application._id,
                decision,
                employerName,
                feedback
            })

            showSuccess(decision === 'approved'
                ? 'Work approved! Payment will be released to the worker.'
                : 'Changes requested. The worker has been notified.'
            )
        } catch (error: any) {
            console.error('Failed to submit review:', error)
            const errorDetails = error.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : error.response?.data?.error?.message || 'Failed to submit review'
            showError(errorMessage)
            throw error // Re-throw to let modal handle it
        }
    }

    const filteredApplications = (applications || [])
        .filter(() => {
            // For employer dashboard, we show applications even if gig is not populated
            // This allows management of orphaned applications
            return true // Always include applications for employer management
        }) // Include all applications for management
        .filter(app => statusFilter === 'all' || app.status === statusFilter)
        .filter(app => {
            if (!searchQuery.trim()) return true
            const searchLower = searchQuery.toLowerCase()

            // Handle cases where gig might not be populated
            let gigMatch = false
            if (isGigPopulated(app.gig)) {
                const gig = app.gig as Gig
                const categoryString = getCategoryName(gig.category)
                gigMatch = gig.title.toLowerCase().includes(searchLower) ||
                    categoryString.toLowerCase().includes(searchLower)
            }

            // Always check worker name if available
            const workerMatch = typeof app.worker === 'object' &&
                `${app.worker.profile.firstName} ${app.worker.profile.lastName}`.toLowerCase().includes(searchLower)

            return gigMatch || workerMatch
        })

    const sortedApplications = filteredApplications.sort((a, b) => {
        switch (sortBy) {
            case 'newest':
                return new Date(b.appliedAt || b.createdAt).getTime() - new Date(a.appliedAt || a.createdAt).getTime()
            case 'oldest':
                return new Date(a.appliedAt || a.createdAt).getTime() - new Date(b.appliedAt || b.createdAt).getTime()
            case 'amount':
                return (b.proposedRate || 0) - (a.proposedRate || 0)
            default:
                return 0
        }
    })

    const getStatusIcon = (status: Application['status']) => {
        switch (status) {
            case 'pending':
                return <FiClock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" />
            case 'accepted':
                return <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
            case 'in_progress':
                return <FiTrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" />
            case 'submitted':
                return <FiMessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500" />
            case 'under_review':
                return <FiEye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500" />
            case 'approved':
                return <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
            case 'rejected':
                return <FiXCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
            case 'completed':
                return <FiCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" />
            default:
                return <FiAlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
        }
    }

    const getStatusColor = (status: Application['status']) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
            case 'accepted':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            case 'in_progress':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
            case 'submitted':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
            case 'under_review':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
        }
    }

    const isWorker = user?.role === 'worker'
    const isEmployer = user?.role === 'employer'

    // sortedApplications already has the populated filter applied, no need to filter again

    // Stats for dashboard summary
    const stats = [
        {
            label: 'Total',
            value: applications.length,
            icon: FiBriefcase,
            color: 'text-blue-600 dark:text-blue-400'
        },
        {
            label: 'Pending',
            value: applications.filter(app => app.status === 'pending').length,
            icon: FiClock,
            color: 'text-yellow-600 dark:text-yellow-400'
        },
        {
            label: 'Accepted',
            value: applications.filter(app => app.status === 'accepted').length,
            icon: FiCheckCircle,
            color: 'text-green-600 dark:text-green-400'
        },
        {
            label: 'Success Rate',
            value: applications.length > 0 ? Math.round((applications.filter(app => app.status === 'accepted').length / applications.length) * 100) : 0,
            icon: FiTrendingUp,
            color: 'text-purple-600 dark:text-purple-400',
            suffix: '%'
        }
    ]

    const statusTabs = [
        { id: 'all', label: 'All', count: applications.length },
        { id: 'pending', label: 'Pending', count: applications.filter(app => app.status === 'pending').length },
        { id: 'accepted', label: 'Accepted', count: applications.filter(app => app.status === 'accepted').length },
        { id: 'in_progress', label: 'In Progress', count: applications.filter(app => app.status === 'in_progress').length },
        { id: 'submitted', label: 'Submitted', count: applications.filter(app => app.status === 'submitted').length },
        { id: 'under_review', label: 'Under Review', count: applications.filter(app => app.status === 'under_review').length },
        { id: 'approved', label: 'Approved', count: applications.filter(app => app.status === 'approved').length },
        { id: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length },
        { id: 'completed', label: 'Completed', count: applications.filter(app => app.status === 'completed').length }
    ]

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                        <LoadingSpinner size="xl" />
                        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading applications...</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full max-w-none overflow-hidden">
            <Helmet>
                <title>{isWorker ? 'My Applications' : 'Applications Received'} - SideGO</title>
                <meta name="description" content="Manage your gig applications and track their status" />
            </Helmet>

            {/* Page Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between gap-4 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {isWorker ? 'My Applications' : 'Applications Received'}
                    </h1>
                    <button
                        onClick={handleRefreshApplications}
                        disabled={isRefreshing}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh applications"
                    >
                        <FiRefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {isWorker
                        ? 'Track the status of your gig applications'
                        : 'Review and manage applications for your posted gigs'
                    }
                </p>

                {/* Stats grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon
                        return (
                            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                            {stat.label}
                                        </p>
                                        <p className={`text-xl font-semibold ${stat.color}`}>
                                            {stat.value}{stat.suffix || ''}
                                        </p>
                                    </div>
                                    <Icon className={`w-6 h-6 ${stat.color}`} />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Search and filters */}
            <div className="mb-6">
                {/* Search bar */}
                <div className="relative mb-4">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search ${isWorker ? 'your applications' : 'applications'}...`}
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm sm:text-base"
                    />
                </div>

                {/* Controls - Sorting Dropdown */}
                <div className="flex items-center justify-end">
                    <SelectDropdown
                        value={sortBy}
                        onChange={(value) => setSortBy(value as any)}
                        options={[
                            { value: 'newest', label: 'Newest First' },
                            { value: 'oldest', label: 'Oldest First' },
                            { value: 'amount', label: 'Highest Amount' }
                        ]}
                        placeholder="Sort by..."
                        className="w-full sm:w-auto"
                    />
                </div>

                {/* Status tabs - Mobile-friendly horizontal scrolling */}
                <div className="mt-6">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="overflow-x-auto scrollbar-hide">
                            <nav className="flex space-x-4 sm:space-x-8 -mb-px min-w-max" role="tablist">
                                {statusTabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setStatusFilter(tab.id as any)
                                        }}
                                        role="tab"
                                        aria-selected={statusFilter === tab.id ? 'true' : 'false'}
                                        className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-1.5 sm:gap-2 transition-colors flex-shrink-0 ${statusFilter === tab.id
                                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                            }`}
                                    >
                                        <span>{tab.label}</span>
                                        {tab.count > 0 && (
                                            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-1 sm:px-1.5 rounded-full text-xs">
                                                {tab.count}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Applications List - Mobile-first design */}
            {sortedApplications.length > 0 ? (
                <div className="space-y-4">
                    {sortedApplications.map((application) => {
                        const gig = isGigPopulated(application.gig) ? application.gig as Gig : null
                        const isExpanded = expandedCard === application._id

                        return (
                            <div
                                key={application._id}
                                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow w-full max-w-none"
                            >
                                {/* Card Header - Always visible */}
                                <div className="p-4 sm:p-6">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            {gig ? (
                                                <Link
                                                    to={`/gigs/${gig._id}`}
                                                    className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors block truncate"
                                                >
                                                    {gig.title}
                                                </Link>
                                            ) : (
                                                <div className="text-lg sm:text-xl font-semibold text-gray-500 dark:text-gray-400 block truncate">
                                                    [Gig No Longer Available]
                                                </div>
                                            )}

                                            {gig && (
                                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <FiBriefcase className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                        <span className="capitalize truncate">{getCategoryName(gig.category)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <FiMapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                        <span className="truncate">{gig.location.location}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <span className="truncate">
                                                            <CurrencyDisplay
                                                                amount={gig.payment.amount}
                                                                currency={gig.payment.currency}
                                                                compact
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col items-end gap-2 ml-3">
                                            <div className="flex items-center gap-1.5">
                                                {getStatusIcon(application.status)}
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                                                    {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                                                </span>
                                            </div>

                                            {/* Compact countdown for accepted applications (Workers only) */}
                                            {isWorker && application.status === 'accepted' && gig && gig.duration && (
                                                <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-md">
                                                    <FiClock className="w-3 h-3" />
                                                    <div className="font-medium">
                                                        <CountdownTimer
                                                            startTime={application.updatedAt}
                                                            duration={gig.duration.estimated}
                                                            endDate={gig.duration.endDate}
                                                            className="text-xs"
                                                            compact={true}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quick info and expand toggle */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                            <div className="flex items-center gap-1">
                                                <FiCalendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                                <span>{formatDate(application.appliedAt || application.createdAt)}</span>
                                            </div>
                                            {application.proposedRate && gig && (
                                                <div className="flex items-center gap-1">
                                                    <span>Rate: <CurrencyDisplay
                                                        amount={application.proposedRate}
                                                        currency={gig.payment.currency}
                                                        compact
                                                    /></span>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => setExpandedCard(isExpanded ? null : application._id)}
                                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                        >
                                            <FiMoreVertical className="w-4 h-4 text-gray-400" />
                                        </button>
                                    </div>
                                </div>

                                {/* Expandable content */}
                                {isExpanded && (
                                    <div className="border-t border-gray-200 dark:border-gray-700">
                                        <div className="p-4 sm:p-6 space-y-6">
                                            {/* Application Message */}
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                                    {isWorker ? 'Your Message:' : 'Application Message:'}
                                                </h4>
                                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                                        {application.message}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Countdown Timer for Accepted Applications (Workers only) */}
                                            {isWorker && application.status === 'accepted' && gig && gig.duration && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                                        Project Deadline:
                                                    </h4>
                                                    <CountdownTimer
                                                        startTime={application.updatedAt} // When the application was accepted
                                                        duration={gig.duration.estimated}
                                                        endDate={gig.duration.endDate}
                                                        className="max-w-sm"
                                                    />
                                                </div>
                                            )}

                                            {/* Additional Details */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {application.estimatedDuration && (
                                                    <div>
                                                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Duration:</span>
                                                        <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium mt-1">
                                                            {application.estimatedDuration}
                                                        </p>
                                                    </div>
                                                )}

                                                {application.availability && (
                                                    <div>
                                                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Availability:</span>
                                                        <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium mt-1">
                                                            {application.availability}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Worker Profile (for employers) */}
                                            {isEmployer && typeof application.worker === 'object' && (
                                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <Avatar
                                                            avatarId={application.worker.profile.avatar}
                                                            alt={`${application.worker.profile.firstName} ${application.worker.profile.lastName}`}
                                                            size="md"
                                                            className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 dark:bg-primary-900 flex-shrink-0"
                                                            fallbackIcon={<FiUser className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                                                                {`${application.worker.profile.firstName} ${application.worker.profile.lastName}`}
                                                            </h4>
                                                            {application.worker.ratings.average && (
                                                                <div className="flex items-center gap-1">
                                                                    <FiStar className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-current" />
                                                                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                                                        {application.worker.ratings.average.toFixed(1)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Action buttons for employers */}
                                                    <div className="space-y-2">
                                                        {application.status === 'pending' && (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                <button
                                                                    onClick={() => handleStatusUpdate(application._id, 'accepted')}
                                                                    className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                                                >
                                                                    <FiCheck className="w-4 h-4" />
                                                                    Accept
                                                                </button>
                                                                <button
                                                                    onClick={() => handleStatusUpdate(application._id, 'rejected')}
                                                                    className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                                                >
                                                                    <FiX className="w-4 h-4" />
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Review Work button for submitted applications */}
                                                        {(application.status === 'submitted' || application.status === 'under_review') && application.workSubmission && (
                                                            <button
                                                                onClick={() => handleOpenReview(
                                                                    application,
                                                                    typeof application.worker === 'object' ? `${application.worker.profile?.firstName} ${application.worker.profile?.lastName}`.trim() || 'Worker' : 'Worker',
                                                                    gig ? gig.title : 'Gig'
                                                                )}
                                                                className="w-full px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                                            >
                                                                <FiEye className="w-4 h-4" />
                                                                Review Work
                                                            </button>
                                                        )}

                                                        {/* Escrow Status Display */}
                                                        {application.escrow && (
                                                            <div className="mt-4">
                                                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                                                    Escrow Status
                                                                </h4>
                                                                <EscrowStatus
                                                                    escrowId={application.escrow.escrowId}
                                                                    escrowStatus={application.escrow.escrowStatus}
                                                                    amountHeld={application.escrow.amountHeld}
                                                                    currency={isGigPopulated(application.gig) ? (application.gig as Gig).payment?.currency || 'USD' : 'USD'}
                                                                    releasedAt={application.escrow.releasedAt?.toString()}
                                                                    autoApprovalDeadline={application.review?.autoApprovalDeadline?.toString()}
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Approved/Rejected status display */}
                                                        {(application.status === 'approved' || application.status === 'rejected') && (
                                                            <div className={`w-full px-3 py-2 rounded-lg text-center font-medium flex items-center justify-center gap-2 ${
                                                                application.status === 'approved'
                                                                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                                                                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                                                            }`}>
                                                                {application.status === 'approved' ? (
                                                                    <>
                                                                        <FiCheckCircle className="w-4 h-4" />
                                                                        Work Approved - Payment Released
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FiXCircle className="w-4 h-4" />
                                                                        Changes Requested
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                            <button
                                                                onClick={() => handleStartConversation(
                                                                    typeof application.worker === 'object' ? application.worker._id : application.worker,
                                                                    typeof application.worker === 'object' ? `${application.worker.profile?.firstName} ${application.worker.profile?.lastName}`.trim() || 'Worker' : 'Worker',
                                                                    gig ? gig.title : '[Gig No Longer Available]',
                                                                    application._id
                                                                )}
                                                                className="w-full px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                                            >
                                                                <FiMessageSquare className="w-4 h-4" />
                                                                Message
                                                            </button>
                                                            <Link
                                                                to={`/profile/${application.worker._id}`}
                                                                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                                            >
                                                                <FiEye className="w-4 h-4" />
                                                                View Profile
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Actions for Workers */}
                                            {isWorker && (
                                                <div className="flex flex-col sm:flex-row gap-3">
                                                    {/* Submit Work button for accepted applications */}
                                                    {application.status === 'accepted' && gig && (gig as Gig).requireSubmission !== false && (
                                                        <button
                                                            onClick={() => handleOpenWorkSubmission(application, gig ? gig.title : 'Gig')}
                                                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center font-medium flex items-center justify-center gap-2"
                                                        >
                                                            <FiCheck className="w-4 h-4" />
                                                            Submit Work
                                                        </button>
                                                    )}

                                                    {/* On-site work completion for accepted applications */}
                                                    {application.status === 'accepted' && gig && (gig as Gig).requireSubmission === false && (
                                                        <div className="flex-1 px-4 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded-lg text-center font-medium flex items-center justify-center gap-2">
                                                            <FiMapPin className="w-4 h-4" />
                                                            Complete On-site Work
                                                        </div>
                                                    )}

                                                    {/* Review Work button for submitted applications */}
                                                    {(application.status === 'submitted' || application.status === 'under_review') && application.workSubmission && (
                                                        <div className="flex-1 px-4 py-2 bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 rounded-lg text-center font-medium flex flex-col items-center justify-center gap-1">
                                                            <div className="flex items-center gap-2">
                                                                <FiEye className="w-4 h-4" />
                                                                Work Submitted
                                                            </div>
                                                            {application.status === 'submitted' && application.review?.autoApprovalDeadline && (
                                                                <div className="text-xs opacity-75">
                                                                    Auto-approval in <CountdownTimer
                                                                        startTime={application.workSubmission.submittedAt.toString()}
                                                                        duration="14 days"
                                                                        className="text-xs"
                                                                        compact={true}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Approved/Rejected status */}
                                                    {(application.status === 'approved' || application.status === 'rejected') && (
                                                        <div className={`flex-1 px-4 py-2 rounded-lg text-center font-medium flex items-center justify-center gap-2 ${
                                                            application.status === 'approved'
                                                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                                                                : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                                                        }`}>
                                                            {application.status === 'approved' ? (
                                                                <>
                                                                    <FiCheckCircle className="w-4 h-4" />
                                                                    Work Approved
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiXCircle className="w-4 h-4" />
                                                                    Changes Requested
                                                                </>
                                                            )}
                                                        </div>
                                                    )}

                                                    {gig ? (
                                                        <>
                                                            <Link
                                                                to={`/gigs/${gig._id}`}
                                                                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-center font-medium"
                                                            >
                                                                View Gig Details
                                                            </Link>
                                                            <button
                                                                onClick={() => handleMessageEmployer(
                                                                    typeof gig.employer === 'object' ? gig.employer._id : gig.employer,
                                                                    typeof gig.employer === 'object' ? `${gig.employer.profile?.firstName} ${gig.employer.profile?.lastName}`.trim() || 'Employer' : 'Employer',
                                                                    gig.title,
                                                                    application._id
                                                                )}
                                                                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium flex items-center justify-center gap-2"
                                                            >
                                                                <FiMessageSquare className="w-4 h-4" />
                                                                Message Client
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-center font-medium">
                                                            Gig no longer available
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            ) : (
                // Enhanced empty state
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 sm:p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiBriefcase className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        No applications found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                        {searchQuery || statusFilter !== 'all'
                            ? 'Try adjusting your filters to see more applications.'
                            : isWorker
                                ? 'You haven\'t applied for any gigs yet. Start browsing to find opportunities!'
                                : 'No applications received yet. Make sure your gigs are attractive to workers!'
                        }
                    </p>
                    {!searchQuery && statusFilter === 'all' && (
                        <Link
                            to={isWorker ? '/gigs' : '/dashboard/gigs/create'}
                            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            {isWorker ? (
                                <>
                                    <FiSearch className="w-4 h-4 mr-2" />
                                    Browse Gigs
                                </>
                            ) : (
                                <>
                                    <FiBriefcase className="w-4 h-4 mr-2" />
                                    Post a Gig
                                </>
                            )}
                        </Link>
                    )}
                </div>
            )}

            {/* Message Modal */}
            <MessageModal
                isOpen={messageModal.isOpen}
                onClose={handleCloseMessageModal}
                onSend={handleSendMessage}
                recipientName={messageModal.recipientName}
                context={messageModal.context}
            />

            {/* Work Submission Modal */}
            <WorkSubmissionModal
                isOpen={workSubmissionModal.isOpen}
                onClose={handleCloseWorkSubmissionModal}
                onSubmit={handleSubmitWork}
                gigTitle={workSubmissionModal.gigTitle}
            />

            {/* Review Modal */}
            <ReviewModal
                isOpen={reviewModal.isOpen}
                onClose={handleCloseReviewModal}
                onReview={handleSubmitReview}
                workSubmission={reviewModal.application?.workSubmission || { deliveryUrls: [], description: '', submittedAt: new Date() }}
                workerName={reviewModal.workerName}
                gigTitle={reviewModal.gigTitle}
            />
        </div>
    )
}

export default ApplicationsPage